import { ApexOptions } from 'apexcharts';
import { ComponentType, Suspense, lazy, useMemo } from 'react';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import { formatNumberWithCommas, goodFormatNumber } from '../utils';
import { Spinner } from './Spinner';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<ReactApexChartProps>;

function hexToRgb(hex: string): [number, number, number] {
  const parsed = hex.replace('#', '');
  const bigint = parseInt(parsed, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function getColor(
  y: number,
  minY: number,
  maxY: number,
  colorNegative: string,
  colorPositive: string,
  colorNeutral: string,
): string {
  const [r, g, b] = hexToRgb(colorNeutral);
  if (y === 0) return `rgba(${r}, ${g}, ${b}, 1)`;

  if (y > 0) {
    const maxPositive = maxY > 0 ? maxY : 1;
    let factor = Math.pow(y / maxPositive, 0.25);
    if (factor < 0.2) factor = 0.2;
    if (factor > 1) factor = 1;

    const [r, g, b] = hexToRgb(colorPositive);
    return `rgba(${r}, ${g}, ${b}, ${factor})`;
  } else {
    const maxNegative = Math.abs(minY);
    let factor = Math.pow(Math.abs(y) / maxNegative, 0.5);
    if (factor < 0.2) factor = 0.2;
    if (factor > 1) factor = 1;

    const [r, g, b] = hexToRgb(colorNegative);
    return `rgba(${r}, ${g}, ${b}, ${factor})`;
  }
}

export interface TreemapItem {
  id?: number;
  name: string;
  value: number;
  originalValue: number;
  currency?: string;
  pnl_percent: number;
  pnl?: number;
  quantity?: number;
  onClick?: (id: number, name: string) => void;
}

export interface ChartTreemapProps {
  items?: TreemapItem[];
  height?: string;
  colorNegative?: string;
  colorPositive?: string;
  colorNeutral?: string;
}

export const ChartTreemap = ({
  items = [],
  height = '370px',
  colorNegative = '#DC2626',
  colorPositive = '#16A34A',
  colorNeutral = '#9d9d9d',
}: ChartTreemapProps) => {
  const series = useMemo<ApexAxisChartSeries>(() => {
    if (!items.length) return [];

    const totalOriginal = items.reduce((sum, item) => sum + item.value, 0);
    const maxOriginalValue = Math.max(...items.map(item => item.value), 0);
    const MIN_VALUE = maxOriginalValue * 0.03;

    const adjustedItems = items.map(item => ({
      ...item,
      value: +item.value < MIN_VALUE ? MIN_VALUE : +item.value,
    }));

    const totalAdjusted = adjustedItems.reduce((sum, item) => sum + item.value, 0);

    const normalizedItems = adjustedItems.map(item => ({
      ...item,
      value: (item.value / totalAdjusted) * totalOriginal,
    }));

    const values = normalizedItems.map(item => item.pnl_percent);
    const minY = Math.min(...values);
    const maxY = Math.max(...values);

    const series: ApexAxisChartSeries = [
      {
        data: normalizedItems.map(item => ({
          x: item.name,
          y: item.value,
          fillColor: getColor(
            item.pnl_percent,
            minY,
            maxY,
            colorNegative,
            colorPositive,
            colorNeutral,
          ),
        })),
      },
    ];

    return series;
  }, [items, colorNegative, colorPositive, colorNeutral]);

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'treemap',
        height,
        toolbar: {
          show: false,
        },
        selection: {
          enabled: false,
        },
        animations: {
          enabled: true,
          speed: 150,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 150,
          },
        },
        events: {
          click: function (_, __, config) {
            const item = items[config.dataPointIndex];
            if (item && item.id) {
              item?.onClick && item?.onClick(item.id, item.name);
            }
          },
        },
        zoom: {
          enabled: false,
        },
      },
      title: {},
      legend: { show: false },
      plotOptions: {
        treemap: {
          reverseNegativeShade: false,
          borderRadius: 0,
          enableShades: false,
          seriesTitle: {
            show: false,
          },
          distributed: false,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (text: string, op: any) {
          const item = items[op.dataPointIndex];
          if (!item) return text;

          const quantity = item.quantity !== undefined ? formatNumberWithCommas(item.quantity) : '';
          const currency = item?.currency?.toUpperCase();
          const value =
            item.originalValue !== undefined
              ? `${goodFormatNumber(item.originalValue)} ${currency}`
              : '';

          return [item.name, quantity, value];
        },
        offsetY: -11,
        style: {
          fontSize: '14px',
          colors: ['#fff'],
          fontFamily: 'Inter',
        },
      },
      tooltip: {
        custom: function ({ dataPointIndex }) {
          const item = items[dataPointIndex];
          if (!item) return '';

          const formattedPercent = `${item.pnl_percent <= 0 ? '' : '+'}${goodFormatNumber(
            item.pnl_percent,
          )}%`;

          const quantity = item.quantity != undefined ? formatNumberWithCommas(item.quantity) : '—';

          const currency = item?.currency?.toUpperCase();
          const formattedPNL =
            item.pnl != undefined ? `${goodFormatNumber(item.pnl)} ${currency}` : '—';
          const usdt =
            item.originalValue != undefined
              ? `${goodFormatNumber(item.originalValue)} ${currency}`
              : '';

          return `
      <div class="flex flex-col gap-2 justify-center bg-white text-gray-800 text-sm p-3 rounded-lg shadow font-[Inter]">
      <div><strong>Quantity:</strong> ${quantity}</div>
        <div><strong>PNL:</strong> ${formattedPNL}</div>
        <div><strong>PNL Percent:</strong> ${formattedPercent}</div>
        <div><strong>Total:</strong> ${usdt}</div>
      </div>
    `;
        },
      },
    }),
    [height, items],
  );

  return (
    <div className="w-full">
      <Suspense
        fallback={
          <div className="min-h-[250px] h-full w-full flex justify-center items-center">
            <Spinner size="lg" />
          </div>
        }
      >
        <Chart options={options} series={series} type="treemap" height={height} />
      </Suspense>
    </div>
  );
};
